import React from "react";
import { UserRole } from "@it-tc/legacy-api";
import { Nav, NavExpandable, NavItem, NavList } from "@patternfly/react-core";
import { useFlag } from "@unleash/proxy-client-react";
import { Link, Location, useLocation } from "react-router-dom";
import useRole from "../../shared/hooks/user/useRole";
import {
  type Permission,
  Permissions,
} from "../../../features/api/permissionsApi";
import isNotUndefined from "../../shared/type-guards/is-not-undefined";
import usePermissions from "../../shared/hooks/user/usePermissions";

type NavElement = NavLinkElement | NavExpandableElement;
type NavExpandableElement = {
  display: string;
  group: NavElement[];
};
type NavLinkElement = {
  to: string;
  display: string;
  enabled(userRole: UserRole | null, permissions: Set<Permission>): boolean;
};

export default ApplicationSidebarNav;

function ApplicationSidebarNav() {
  const role = useRole();
  const { data: permissions } = usePermissions();
  const location = useLocation();

  const enableManageCountriesRoute = useFlag("enable-manage-countries-route");
  const enableManageGlobalAdministratorsRoute = useFlag(
    "enable-manage-global-administrators-route",
  );
  const enableManageSiteAdministratorsRoute = useFlag(
    "enable-manage-site-administrators-route",
  );
  const enableManageRegionalAdministratorsRoute = useFlag(
    "enable-manage-regional-administrators-route",
  );
  const enableManageTestingCentersRoute = useFlag(
    "enable-manage-testing-centers-route",
  );
  const enableEligibilityMessagesRoute = useFlag(
    "enable-eligibility-messages-route",
  );
  const enableGovernmentIdentificationApprovalRoute = useFlag(
    "enable-government-identification-approval-route",
  );
  const enableManageChatIdleTimeRoute = useFlag(
    "enable-manage-chat-idle-time-route",
  );
  const enableManageProctorAlertRoute = useFlag(
    "enable-manage-proctor-alert-route",
  );
  const enableUniversalHoursOfOperation = useFlag(
    "enable-universal-hours-of-operation-route",
  );
  const enableConcurrentExamsRoute = useFlag("enable-concurrent-exams-route");

  const navElements: NavElement[] = [
    {
      to: "external/dashboard",
      display: "Dashboard",
      enabled(userRole) {
        const allowRoles = new Set<UserRole | null>(["CANDIDATE"]);
        return allowRoles.has(userRole);
      },
    },
    {
      to: "external/public/testing-locations",
      display: "Testing Locations",
      enabled(userRole) {
        const allowRoles = new Set<UserRole | null>(["CANDIDATE", null]);
        return allowRoles.has(userRole);
      },
    },
    {
      to: "internal/exam-deliveries",
      display: "Exam Deliveries",
      enabled(userRole) {
        const allowRoles = new Set<UserRole | null>([
          "GLOBAL_ADMINISTRATOR",
          "PROCTOR",
          "REGIONAL_ADMINISTRATOR",
          "SITE_ADMINISTRATOR",
        ]);
        return allowRoles.has(userRole);
      },
    },
    {
      to: "internal/manage-countries",
      display: "Manage Countries",
      group: [
        {
          to: "internal/manage-countries",
          display: "Update",
          enabled(userRole) {
            const allowRoles = new Set<UserRole | null>([
              "GLOBAL_ADMINISTRATOR",
            ]);
            return enableManageCountriesRoute && allowRoles.has(userRole);
          },
        },
        {
          to: "internal/manage-countries/create",
          display: "Create",
          enabled(userRole) {
            const allowRoles = new Set<UserRole | null>([
              "GLOBAL_ADMINISTRATOR",
            ]);
            return enableManageCountriesRoute && allowRoles.has(userRole);
          },
        },
      ],
    },
    {
      to: "internal/manage-testing-centers",
      display: "Manage Testing Centers",
      enabled(userRole) {
        const allowRoles = new Set<UserRole | null>([
          "GLOBAL_ADMINISTRATOR",
          "REGIONAL_ADMINISTRATOR",
        ]);
        return enableManageTestingCentersRoute && allowRoles.has(userRole);
      },
    },
    {
      display: "Manage Administrators",
      group: [
        {
          to: "internal/manage-global-administrators",
          display: "Manage Global Administrators",
          enabled(userRole) {
            const allowRoles = new Set<UserRole | null>([
              "GLOBAL_ADMINISTRATOR",
            ]);
            return (
              enableManageGlobalAdministratorsRoute && allowRoles.has(userRole)
            );
          },
        },
        {
          to: "internal/manage-site-administrators",
          display: "Manage Site Administrators",
          enabled(userRole) {
            const allowRoles = new Set<UserRole | null>([
              "GLOBAL_ADMINISTRATOR",
              "SITE_ADMINISTRATOR",
            ]);
            return (
              enableManageSiteAdministratorsRoute && allowRoles.has(userRole)
            );
          },
        },
        {
          to: "internal/manage-regional-administrators",
          display: "Manage Regional Administrators",
          enabled(userRole) {
            const allowRoles = new Set<UserRole | null>([
              "GLOBAL_ADMINISTRATOR",
              "REGIONAL_ADMINISTRATOR",
              "SITE_ADMINISTRATOR",
            ]);
            return (
              enableManageRegionalAdministratorsRoute &&
              allowRoles.has(userRole)
            );
          },
        },
      ],
    },
    {
      display: "Manage Policies",
      group: [
        {
          display: "Universal Hours of Operation",
          to: "internal/universal-hours-of-operation",
          enabled(userRole) {
            const allowRoles = new Set<UserRole | null>([
              "GLOBAL_ADMINISTRATOR",
            ]);
            return enableUniversalHoursOfOperation && allowRoles.has(userRole);
          },
        },
        {
          display: "Concurrent Exams",
          to: "internal/concurrent-exams",
          enabled(userRole) {
            const allowRoles = new Set<UserRole | null>([
              "GLOBAL_ADMINISTRATOR",
            ]);
            return enableConcurrentExamsRoute && allowRoles.has(userRole);
          },
        },
        {
          display: "Proctor Alert",
          to: "internal/manage-proctor-alert",
          enabled(userRole) {
            const allowRoles = new Set<UserRole | null>([
              "GLOBAL_ADMINISTRATOR",
            ]);
            return enableManageProctorAlertRoute && allowRoles.has(userRole);
          },
        },
        {
          display: "Chat Idle Time",
          to: "internal/manage-chat-idle-time",
          enabled(userRole) {
            const allowRoles = new Set<UserRole | null>([
              "GLOBAL_ADMINISTRATOR",
            ]);
            return enableManageChatIdleTimeRoute && allowRoles.has(userRole);
          },
        },
      ],
    },
    {
      display: "Manage Exams",
      group: [
        {
          to: "internal/manage-exams",
          display: "Update Exams",
          enabled(_, permissions) {
            const required = new Set<Permission>([
              Permissions.ExamsRead,
              Permissions.ExamsUpdate,
            ]);
            return permissions.isSupersetOf(required);
          },
        },
        {
          to: "internal/manage-exams/create",
          display: "Create Exam",
          enabled(_, permissions) {
            const required = new Set<Permission>([Permissions.ExamsCreate]);
            return permissions.isSupersetOf(required);
          },
        },
      ],
    },
    {
      to: "internal/eligibility-messages",
      display: "Eligibility Messages",
      enabled(userRole) {
        const allowRoles = new Set<UserRole | null>(["GLOBAL_ADMINISTRATOR"]);
        return enableEligibilityMessagesRoute && allowRoles.has(userRole);
      },
    },
    {
      to: "internal/government-id-approval",
      display: "Government ID Approval",
      enabled(userRole) {
        const allowRoles = new Set<UserRole | null>([
          "GLOBAL_ADMINISTRATOR",
          "REGIONAL_ADMINISTRATOR",
        ]);
        return (
          enableGovernmentIdentificationApprovalRoute &&
          allowRoles.has(userRole)
        );
      },
    },
  ];
  return (
    <Nav aria-label="Nav">
      <NavList>
        {buildNavElements(navElements, role, permissions, location)}
      </NavList>
    </Nav>
  );
}

function buildNavElements(
  navElements: NavElement[],
  userRole: UserRole | null,
  permissions: Set<Permission>,
  location: Location,
): React.JSX.Element[] {
  return navElements
    .filter((navElement) =>
      isNavElementEnabled(navElement, userRole, permissions),
    )
    .map((navElement) => {
      if (isNavGroupElement(navElement)) {
        return (
          <NavExpandable
            title={navElement.display}
            key={navElement.display}
            isExpanded={isNavElementActive(navElement, location)}
          >
            {buildNavElements(
              navElement.group,
              userRole,
              permissions,
              location,
            )}
          </NavExpandable>
        );
      } else if (isNavLinkElement(navElement)) {
        return (
          <NavItem
            key={navElement.to}
            itemId={navElement.to}
            isActive={isNavElementActive(navElement, location)}
          >
            <Link to={navElement.to}>{navElement.display}</Link>
          </NavItem>
        );
      }
      return undefined;
    })
    .filter(isNotUndefined);
}

function isNavGroupElement(
  navElement: NavElement,
): navElement is NavExpandableElement {
  return "group" in navElement;
}

function isNavLinkElement(
  navElement: NavElement,
): navElement is NavLinkElement {
  return "to" in navElement;
}

/*
  Unfortunately the lowest effort most straight forward way I could come up
  with to determine what nav elements to display involved recusive calls until
  hitting a NavLinkElement. 
  
  Basically this should traverse the nav tree and determine what to show based
  off of whether it has any children that could be shown.
*/
function isNavElementEnabled(
  navElement: NavElement,
  userRole: UserRole | null,
  permissions: Set<Permission>,
): boolean {
  if (isNavGroupElement(navElement)) {
    return navElement.group.some((navElement) =>
      isNavElementEnabled(navElement, userRole, permissions),
    );
  } else if (isNavLinkElement(navElement)) {
    return navElement.enabled(userRole, permissions);
  }
  return false;
}

function isNavElementActive(
  navElement: NavElement,
  location: Location,
): boolean {
  if (isNavGroupElement(navElement)) {
    return navElement.group.some((navElement) =>
      isNavElementActive(navElement, location),
    );
  } else if (isNavLinkElement(navElement)) {
    const baseUrl = new URL(
      import.meta.env.BASE_URL + "/",
      window.location.origin,
    );
    const currentUrl = new URL(location.pathname, baseUrl);
    const toUrl = new URL(navElement.to, baseUrl);
    return currentUrl.toJSON() === toUrl.toJSON();
  }
  return false;
}
